import { ChangeDetectorRef, Component, ComponentRef, ElementRef, NgZone,
         OnDestroy, ViewChild
       } from '@angular/core';

import { Point } from '@svgdotjs/svg.js';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { RegionLike } from 'core/geometry';
import { IconName } from 'core/icons';
import { Utils } from 'core/utils';
import { TextPlaceholder } from '../text-placeholder';

export enum Action
{
  DUPLICATE = 'duplicate',
  EDIT_TEXT = 'edit_text',
  DELETE = 'delete',
  ROTATE = 'rotate',
}

const ACTION_ICONS: Record<Action, IconName> = {
  [Action.DELETE]: 'remove',
  [Action.DUPLICATE]: 'duplicate',
  [Action.EDIT_TEXT]: 'text-editing',
  [Action.ROTATE]: 'rotate',
};

interface RotatedRegion extends RegionLike
{
  rotation: number;
}

interface Trigger
{
  icon: IconName;
  action: Action;
  isActive: boolean;
}

interface TriggerGroup
{
  triggers: Array<Trigger>;
  top: string;
  left: string;
  alignVertical: string;
  alignHorizontal: string;
}

enum FittingSidePosition
{
  Top,
  Bottom,
}

@Component({
    selector: 'action-bar',
    templateUrl: './action-bar.component.html',
    styleUrls: ['./action-bar.component.scss'],
    standalone: false
})
export class ActionBarComponent implements OnDestroy
{
  private static readonly SCREEN_EDGE_DISTANCE: number = 8; /* px */

  public componentRef: ComponentRef<ActionBarComponent>;
  public nativeElement: HTMLElement;
  public targetRegion: RotatedRegion | undefined;
  public placeholder: TextPlaceholder | undefined;
  public triggerGroup: TriggerGroup | undefined;

  @ViewChild('triggersRef') private triggersRef: ElementRef;

  private triggerSide: FittingSidePosition = FittingSidePosition.Bottom;

  public rotationGroup: TriggerGroup | undefined = undefined;

  private screenEdgeShift: number = 0;
  private screenBoxWatch: number | undefined = undefined;
  private screenBox: string = '';

  constructor(
      element: ElementRef,
      private cdr: ChangeDetectorRef,
      private toolboxService: ToolboxService,
      private zone: NgZone,
  ) {
    this.nativeElement = element.nativeElement;
  }

  public initialize(placeholder: TextPlaceholder): void
  {
    this.nativeElement.addEventListener(
        'mouseleave', this.mouseLeave.bind(this));

    this.placeholder = placeholder;
    this.setTargetRegion();
    this.updateFittingSide();
    this.zone.runOutsideAngular(() => this.respondToScreenBoxChange());
  }

  public setTargetRegion(): void
  {
    if (!this.placeholder) {
      return;
    }

    this.targetRegion = this.calculateTargetRegion();

    if (Utils.ChangeDetection.isPaused) {
      this.cdr.detectChanges();
    }
  }

  private calculateTargetRegion(): RotatedRegion
  {
    const transform = (p: any, matrix: DOMMatrix) =>
      p.transform(matrix) as any;

    const wrapper = this.placeholder.wrapper;
    const wrapperCTM = DOMMatrix.fromMatrix(
        (<any>wrapper).parent().node.getScreenCTM());

    const x = wrapper.x() as number;
    const y = wrapper.y() as number;
    const w = wrapper.width() as number;
    const h = wrapper.height() as number;

    const rotation = (() => {
      const tl = transform(new Point(x, y), wrapperCTM);
      const tr = transform(new Point(x + w, y), wrapperCTM);
      return Utils.rotationToOrigin(tr.x - tl.x, tr.y - tl.y);
    })();

    const stableCTM = wrapperCTM.multiply(new DOMMatrix()
      .translate(wrapper.cx(), wrapper.cy())
      .rotate(-rotation)
      .translate(-wrapper.cx(), -wrapper.cy()));

    const topLeft = transform(new Point(x, y), stableCTM);
    const bottomRight = transform(new Point(x + w, y + h), stableCTM);

    const off = Utils.getTotalOffset(
        this.nativeElement.offsetParent as HTMLElement);

    return {
      x: topLeft.x - off.left,
      y: topLeft.y - off.top,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
      rotation: rotation,
    };
  }

  private respondToScreenBoxChange(): void
  {
    const isPlaced = !!this.nativeElement.offsetParent;

    if (this.placeholder && isPlaced && !Utils.ChangeDetection.isPaused) {
      const box = this.placeholder.wrapper.node.getBoundingClientRect();
      const screenBox = [box.left, box.top, box.width, box.height].join();

      if (screenBox !== this.screenBox) {
        this.screenBox = screenBox;
        this.setTargetRegion();
        this.updateFittingSide();
        this.cdr.detectChanges();
      }
    }

    this.screenBoxWatch = window.requestAnimationFrame(
        () => this.respondToScreenBoxChange());
  }

  public updateFittingSide(): void
  {
    if (!this.placeholder) {
      return;
    }

    this.screenEdgeShift = 0;
    this.applyTriggerSide(FittingSidePosition.Bottom);
    if (!this.checkTriggersAreUseable()) {
      this.applyTriggerSide(FittingSidePosition.Top);
    }

    this.applyScreenEdgeShift();
    this.attachRotationControl();
  }

  private applyTriggerSide(side: FittingSidePosition): void
  {
    this.triggerSide = side;
    this.updateTriggerGroup();
    this.cdr.detectChanges();
  }

  private checkTriggersAreUseable(): boolean
  {
    if (!(this.triggersRef && this.placeholder)) {
      return false;
    }

    const designArea = ((<any>this.placeholder.wrapper)
                        .root()
                        .node
                        .getBoundingClientRect());
    const hitBox = this.triggersRef.nativeElement.getBoundingClientRect();

    return hitBox.bottom <= designArea.bottom;
  }

  private applyScreenEdgeShift(): void
  {
    if (!this.triggersRef) {
      return;
    }

    const distance = ActionBarComponent.SCREEN_EDGE_DISTANCE;
    const screenWidth = document.documentElement.clientWidth;
    const hitBox = this.triggersRef.nativeElement.getBoundingClientRect();

    this.screenEdgeShift = hitBox.left < distance
      ? distance - hitBox.left
      : (hitBox.right > screenWidth - distance
          ? screenWidth - distance - hitBox.right
          : 0);

    if (this.screenEdgeShift !== 0) {
      this.updateTriggerGroup();
      this.cdr.detectChanges();
    }
  }

  private attachRotationControl(): void
  {
    this.placeholder.attachRotationControl(
        this.nativeElement.querySelector<HTMLElement>('.rotation-trigger') ||
        undefined);
  }

  private updateTriggerGroup(): void
  {
    const positioningMethod = this.triggerSide === FittingSidePosition.Top
      ? positionTop
      : positionBottom;
    const group = positioningMethod(this.getTriggers());

    this.rotationGroup =
        this.triggerSide !== FittingSidePosition.Top &&
        this.placeholder.hasTransformHandle()
      ? positionTop([triggerOf(Action.ROTATE)])
      : undefined;

    const rotation = (this.targetRegion ? this.targetRegion.rotation : 0) *
        Math.PI / 180;
    const shiftX = this.screenEdgeShift * Math.cos(rotation);
    const shiftY = -this.screenEdgeShift * Math.sin(rotation);

    this.triggerGroup = {
      ...group,
      alignHorizontal: `calc(${group.alignHorizontal} + ${shiftX}px)`,
      alignVertical: `calc(${group.alignVertical} + ${shiftY}px)`,
    };
  }

  private mouseLeave(event: MouseEvent): void
  {
    this.placeholder.mouseLeave(event);
  }

  private getTriggers(): Array<Trigger>
  {
    const isFlexible = this.placeholder.isFlexible();
    const isMovable = this.placeholder.hasTransformHandle();
    const actions: Array<Action> = [];

    if (this.triggerSide === FittingSidePosition.Top && isMovable) {
      actions.push(Action.ROTATE);
    }
    if (isFlexible) {
      actions.push(Action.DUPLICATE);
    }
    actions.push(Action.EDIT_TEXT);
    if (isFlexible) {
      actions.push(Action.DELETE);
    }

    return actions.map(action => ({
      ...triggerOf(action),
      isActive: action === Action.EDIT_TEXT && this.placeholder.isTextEditing,
    }));
  }

  public async triggerClicked(action: Action): Promise<void>
  {
    switch (action) {
      case Action.DUPLICATE:
        this.toolboxService.duplicatePlaceholder(this.placeholder);
        break;

      case Action.EDIT_TEXT:
        if (this.placeholder.isTextEditing) {
          this.placeholder.leaveTextEditing();
        }
        else {
          this.placeholder.enterTextEditing();
        }
        break;

      case Action.DELETE:
        this.placeholder.remove();
        break;

      default: break;
    }
  }

  public destroy(): void
  {
    this.nativeElement.removeEventListener('mouseleave', this.mouseLeave);
    this.placeholder.attachRotationControl(undefined);
    this.componentRef.destroy();
  }

  public ngOnDestroy(): void
  {
    window.cancelAnimationFrame(this.screenBoxWatch);
  }
}

function triggerOf(action: Action): Trigger
{
  return { icon: ACTION_ICONS[action], action, isActive: false };
}

function positionTop(triggers: Array<Trigger>): TriggerGroup
{
  return {
    triggers: triggers,
    top: '-8px',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '-100%',
  };
}

function positionBottom(triggers: Array<Trigger>): TriggerGroup
{
  return {
    triggers: triggers,
    top: '100%',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '8px',
  };
}
